<template>
  <IconButton
    v-if="isPreviewable"
    icon="fa-regular fa-eye"
    size="small"
    :tooltip="`Preview ${fileName}`"
    @click="openPreviewModal"
  />

  <!-- File Preview Modal -->
  <Teleport to="body">
    <ModalCard
      v-if="isModalOpen"
      :title="`Preview: ${fileName}`"
      :handle-close="closePreviewModal"
      width="80%"
      height="90%"
    >
      <template #body>
        <div class="file-preview-content">
          <template v-if="isLoading">
            <span class="icon is-large">
              <i class="fa-solid fa-spinner fa-spin"></i>
            </span>
            <span>Loading file content...</span>
          </template>
          <template v-else-if="fileContent">
            <div v-if="canToggleRaw" class="preview-toolbar">
              <button
                class="button is-small is-primary is-outlined"
                @click="showRawContent = !showRawContent"
              >
                <span class="icon">
                  <i
                    class="fa-solid"
                    :class="showRawContent ? 'fa-eye' : 'fa-code'"
                  ></i>
                </span>
                <span>
                  {{
                    showRawContent
                      ? activePlugin.label || 'Rendered'
                      : 'Show Raw'
                  }}
                </span>
              </button>
            </div>
            <component
              :is="activePlugin.component"
              v-if="activePlugin && !showRawContent"
              :file="normalizedFile"
              :content="fileContent.content"
              :parsed="pluginParsed"
              class="workflow-file-plugin"
            />
            <pre v-else class="workflow-file-peek">{{ rawContent }}</pre>
          </template>
          <template v-else>
            <p class="has-text-centered py-4 has-text-grey">
              <span class="icon is-large">
                <i class="fa-solid fa-exclamation-triangle"></i>
              </span>
              <span>Failed to load file preview</span>
            </p>
          </template>
        </div>
      </template>
    </ModalCard>
  </Teleport>
</template>

<script>
import { computed, ref, shallowRef } from 'vue'

import { get, getBlobJson } from '@/api'
import {
  computePeekParams,
  normalizeWorkflowFile,
  pickPlugin,
} from '@/components/workflow-preview-plugins'
export default {
  name: 'WorkflowFilePreview',
  props: {
    file: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const isModalOpen = ref(false)
    const fileContent = ref(null)
    const isLoading = ref(false)
    const showRawContent = ref(false)
    // `shallowRef` so we don't reactively deep-walk the parsed workflow
    // payload (a diagram plugin may hand back a large parsed graph).
    const activePlugin = shallowRef(null)
    const pluginParsed = shallowRef(null)

    // Text formats the host itself knows how to show in the raw <pre>
    // fallback. A file outside this list is still previewable when a
    // plugin claims it (see `isPreviewable`), so registering a renderer
    // for a new extension needs no edit here.
    const TEXT_EXTENSIONS = [
      '.csv',
      '.txt',
      '.json',
      '.gb',
      '.ape',
      '.ga',
      '.md',
      '.yml',
      '.yaml',
      '.py',
      '.r',
      '.sh',
      '.cwl',
      '.wdl',
      '.nf',
      '.xml',
      '.tsv',
      '.bash',
      '.csh',
      '.tcsh',
      '.zsh',
      '.ksh',
    ]

    // Single normalization point — every other "pull this out of a
    // modelmeta wrapper" computed below reads from `normalizedFile`.
    const normalizedFile = computed(() => normalizeWorkflowFile(props.file))
    const fileName = computed(() => normalizedFile.value.name || 'Unknown')
    const fileId = computed(() => normalizedFile.value.id)

    const isPreviewable = computed(() => {
      if (normalizedFile.value.deleted) {
        return false
      }
      const name = fileName.value.toLowerCase()
      if (TEXT_EXTENSIONS.some((ext) => name.endsWith(ext))) {
        return true
      }
      // Anything a plugin claims on metadata alone (images, today) is
      // previewable even though the host can't render it itself.
      return Boolean(computePeekParams(props.file).plugin)
    })

    // Raw fallback: shown when no plugin matched or the user clicked
    // "Show Raw". Plugins own their own rendering; this is just the
    // trailing safety net. Rendered with `{{ }}` interpolation in a
    // <pre>, so the content is escaped by Vue — no v-html, no sanitize
    // pass needed on this path (see plugins for the rendered paths).
    const rawContent = computed(() => {
      const c = fileContent.value?.content
      if (c == null) return ''
      if (typeof c === 'string') return c
      // Binary peeks (image plugin) have no meaningful raw view; their
      // plugin sets `supportsRaw: false` so this shouldn't be reachable.
      if (c instanceof Blob) return ''
      return JSON.stringify(c, null, 2)
    })

    // The Rendered / Raw toggle only makes sense when a plugin is
    // rendering something the raw <pre> could also show.
    const canToggleRaw = computed(
      () =>
        Boolean(activePlugin.value) && activePlugin.value.supportsRaw !== false,
    )

    const loadFilePreview = async () => {
      if (!fileId.value) {
        fileContent.value = null
        return
      }

      isLoading.value = true
      try {
        // Let a metadata-decidable plugin (e.g. markdown for *.md, image
        // for *.png) tell the host what peek params it needs and whether
        // the peek is binary. If nothing matches on metadata alone,
        // `params` is empty and the server's default 10 KB applies.
        const { params, responseType } = computePeekParams(props.file)
        const fetchPeek = responseType === 'blob' ? getBlobJson : get
        const response = await fetchPeek(
          `workflows/workflowfiles/${fileId.value}/peek/`,
          { params },
        )
        if (response.status === 200) {
          fileContent.value = {
            content: response.data,
            data_type: response.data?.data_type,
          }
          const match = pickPlugin({
            file: props.file,
            content: response.data,
          })
          activePlugin.value = match?.plugin ?? null
          pluginParsed.value = match?.parsed ?? null
        }
      } catch (error) {
        console.error('Error loading file preview:', error)
        fileContent.value = null
        activePlugin.value = null
        pluginParsed.value = null
      } finally {
        isLoading.value = false
      }
    }

    const openPreviewModal = () => {
      isModalOpen.value = true
      loadFilePreview()
    }

    const closePreviewModal = () => {
      isModalOpen.value = false
      fileContent.value = null
      showRawContent.value = false
      activePlugin.value = null
      pluginParsed.value = null
    }

    return {
      isModalOpen,
      fileContent,
      isLoading,
      showRawContent,
      fileName,
      isPreviewable,
      rawContent,
      canToggleRaw,
      activePlugin,
      pluginParsed,
      normalizedFile,
      openPreviewModal,
      closePreviewModal,
    }
  },
}
</script>

<style lang="scss" scoped>
.file-preview-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  .preview-toolbar {
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
    padding-bottom: 0.5rem;
  }
  .workflow-file-plugin {
    flex: 1 1 auto;
    width: 100%;
    min-height: 400px;
  }
  .workflow-file-peek {
    flex: 1 1 auto;
    background: #f5f5f5;
    padding: 1rem;
    white-space: pre-wrap;
    overflow: auto;
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.875rem;
  }
}
</style>
